import React, {useEffect, useState} from "react";

import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Dialog} from "primereact/dialog";
import {Tag} from "primereact/tag";
import {Message} from "primereact/message";

import {formatCurrency, formatDecimal, getHostName, getKeyValue, isAdmin, user} from "../../utils/Utils";
import {toDateTimeString} from "../../utils/TimeUtil";

import {AppContext} from "../../HomePage";
import {IndexTemplate} from "../../utils/InlineComponents";
import ProductSelectionBar from "./ProductSelectionBar";
import {CleanupService} from "../../service/CleanupService";
import SiteSearchSummary from "./SiteSearchSummary";
import SuggestionGroups from "./SuggestionGroups";

const SOFT = {
    green: {bg: '#dcfce7', fg: '#15803d', btnBg: '#bbf7d0', btnFg: '#166534'},
    amber: {bg: '#fef3c7', fg: '#b45309', btnBg: '#fde68a', btnFg: '#92400e'},
    red:   {bg: '#fee2e2', fg: '#b91c1c', btnBg: '#fca5a5', btnFg: '#7f1d1d'},
    gray:  {bg: '#f3f4f6', fg: '#6b7280', btnBg: '#e5e7eb', btnFg: '#374151'},
    blue:  {bg: '#dbeafe', fg: '#1d4ed8', btnBg: '#bfdbfe', btnFg: '#1e40af'},
};

// How sure we are that a suggestion is the same product; stored when the suggestion is added.
const STRENGTHS = {
    strong: {soft: SOFT.green, rank: 0, className: 'green'},
    likely: {soft: SOFT.amber, rank: 1, className: 'yellow'},
    weak: {soft: SOFT.red, rank: 2, className: 'red'},
};

const TAG_STYLE = {padding: '.15em .5em', borderRadius: '6px', fontSize: '.8rem', display: 'inline-block'};

// Parent-product code fields used as highlight sources. ref_id is parent-only
// (suggestions don't carry it) but still counts as something to look for.
const PRODUCT_CODE_FIELDS = ['sku', 'mpn', 'ean', 'upc', 'asin', 'barcode', 'ref_id'];
// Codes shorter than this are skipped so a stray 1-2 char value doesn't light up
// unrelated text. Real product identifiers are essentially always longer.
const MIN_CODE_LENGTH = 3;

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const CompetitorSuggestion = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);

    const cleanupService = React.useMemo(() => new CleanupService(), []);

    // Single case-insensitive regex matching any of the parent product's codes.
    // Longer codes are alternated first so e.g. "EC680.R" wins over "EC680".
    const codeHighlightRegex = React.useMemo(() => {
        const product = props.selectedProduct || {};
        const seen = new Set();
        const codes = [];
        PRODUCT_CODE_FIELDS.forEach(field => {
            const value = product[field];
            if (value == null) return;
            const code = String(value).trim();
            if (code.length < MIN_CODE_LENGTH) return;
            const key = code.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            codes.push(code);
        });
        if (codes.length === 0) return null;
        codes.sort((a, b) => b.length - a.length);
        return new RegExp('(' + codes.map(escapeRegExp).join('|') + ')', 'gi');
    }, [props.selectedProduct]);

    // Wrap every occurrence of a parent product code in <mark>; returns the text
    // unchanged when there is no code to match or no match is found.
    const highlightCodes = (text) => {
        if (text == null) return text;
        const str = String(text);
        if (!codeHighlightRegex) return str;
        const parts = str.split(codeHighlightRegex);
        if (parts.length === 1) return str;
        return parts.map((part, i) =>
            i % 2 === 1 ? <mark key={i} className="pux-code-hl">{part}</mark> : part);
    };

    // Profile campaigns use the grouped view: it shows every decision state itself, and its rows
    // carry no match score, so the decision-state and min-score filters do not apply there.
    const groupedView = !!props.campaignProfile;

    const [compareRow, setCompareRow] = useState(null);  // suggestion shown in the compare modal

    const [filters, setFilters] = useState({
        // Preview deep-link shows affected rows (matched or undecided depending on
        // the operation), so default the decision-state chip to "all" in that case.
        is_matched: (props.affectedUrls && props.affectedUrls.length) ? "all" : null,
        name: "",
        used_base_spider: localStorage.getItem("usedBaseSpiderFilter") !== null ? JSON.parse(localStorage.getItem("usedBaseSpiderFilter")) : null,
        min_score: null,
        match_strength: null
    });

    // When an affected-urls deep-link arrives after mount, switch the decision-state
    // chip to "all" so matched (cleanup) and undecided (match/reject) rows both show.
    useEffect(() => {
        if (props.affectedUrls && props.affectedUrls.length) {
            setFilters(f => (f.is_matched === "all" ? f : {...f, is_matched: "all"}));
        }
    }, [props.affectedUrls]);

    const [suggestions, setSuggestions] = useState([]);
    const [selectedSuggestions, setSelectedSuggestions] = useState([]);

    const matchedOptions = [
        {name: t('yes_no.all'), value: "all"},
        {name: t('actions.decision_not_made'), value: null},
        {name: t('actions.seller_added'), value: true, className: "green"},
        {name: t('actions.seller_ignored'), value: false, className: "yellow"}
    ];

    const strengthOptions = [
        {name: t('yes_no.all'), value: null},
        ...Object.keys(STRENGTHS).map(level => ({
            name: t('suggestion.strength_' + level), value: level, className: STRENGTHS[level].className})),
    ];

    const usedBaseSpiderOptions = [
        {name: t('yes_no.all'), value: null},
        {name: "From Base Spider", value: true, className: "green"},
        {name: "Has Own Spider", value: false, className: "yellow"}
    ];

    useEffect(() => {

        // Preview deep-link: when affectedUrls is provided, show ONLY those rows.
        const affectedSet = (props.affectedUrls && props.affectedUrls.length)
            ? new Set(props.affectedUrls) : null;

        const suggestions = props.selectedProduct.competitor_suggestions ? props.selectedProduct.competitor_suggestions.filter(suggestion =>
            ((!filters.name || (filters.name && suggestion.name && suggestion.name.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.product_url && getHostName(suggestion.product_url).toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.sku && suggestion.sku.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.ean && suggestion.ean.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.mpn && suggestion.mpn.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.upc && suggestion.upc.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.barcode && suggestion.barcode.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.price && suggestion.price.value && suggestion.price.value.toString().toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.price && suggestion.price.value && formatDecimal(suggestion.price.value).toString().toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.price && suggestion.price.currency && suggestion.price.currency.toLowerCase().includes(filters.name.toLowerCase()))) ||
                (!filters.name || (filters.name && suggestion.asin && suggestion.asin.toLowerCase().includes(filters.name.toLowerCase())))) &&
                (filters.used_base_spider === null || (filters.used_base_spider === true && suggestion.used_base_spider) || (filters.used_base_spider === false && !suggestion.used_base_spider)) &&
                (groupedView || filters.min_score == null || filters.min_score === '' || (suggestion.match_score != null && suggestion.match_score * 100 >= Number(filters.min_score))) &&
                (filters.match_strength == null || suggestion.match_strength === filters.match_strength) &&

            (groupedView || filters.is_matched === "all" ||
                (filters.is_matched === null && !suggestion.is_match_decision_made) ||
                (filters.is_matched === true && suggestion.is_match_decision_made && suggestion.is_matched) ||
                (filters.is_matched === false && suggestion.is_match_decision_made && !suggestion.is_matched)
            ) &&
            (!affectedSet || affectedSet.has(suggestion.product_url))) : [];

        setSuggestions(suggestions.map(suggestion => ({
            ...suggestion,
            match_strength_rank: STRENGTHS[suggestion.match_strength] ? STRENGTHS[suggestion.match_strength].rank : Object.keys(STRENGTHS).length,
        })));

    }, [props.selectedProduct, filters, props.affectedUrls, groupedView]);

    const onChange = (e) => {
        const {key, value} = getKeyValue(e);
        if (key==="used_base_spider")
            localStorage.setItem("usedBaseSpiderFilter", value);
        setFilters({...filters, [key]: value});
    };

    // Single-select filter chips (product-list look). One option active at a time.
    const setFilter = (key, value) => {
        if (key === "used_base_spider")
            localStorage.setItem("usedBaseSpiderFilter", value);
        setFilters({...filters, [key]: value});
    };
    const chipColor = (opt) => opt.className === 'green' ? 'pux-chip-green'
        : (opt.className === 'yellow' ? 'pux-chip-amber'
            : (opt.className === 'red' ? 'pux-chip-red' : 'pux-chip-blue'));
    const filterChip = (opt, key) => (
        <button key={key + '-' + String(opt.value)} type="button"
                className={"pux-chip pux-chip-toggle " + (filters[key] === opt.value ? chipColor(opt) : 'off')}
                onClick={() => setFilter(key, opt.value)}>{opt.name}</button>
    );

    const addAsCompetitor = (rowData) => {
        let urls = [];
        urls.push({product_url: rowData.product_url});
        props.addCompetitor(urls, null);
    };
    const ignoreCompetitorSuggestion = (rowData) => {
        let urls = [];
        urls.push({product_url: rowData.product_url});
        props.ignoreCompetitorSuggestion(urls);
    };
    const addAllAsCompetitor = () => {
        let urls = [];
        let ss = selectedSuggestions.length > 0 ? selectedSuggestions : suggestions;
        ss.forEach(suggestion => {
            if (!suggestion.is_match_decision_made)
                urls.push({product_url: suggestion.product_url});
        });
        if (urls.length !== 0) {
            props.addCompetitor(urls, null);
            setSelectedSuggestions([]);
        }
    };
    const ignoreAllCompetitorSuggestion = () => {
        let urls = [];
        let ss = selectedSuggestions.length > 0 ? selectedSuggestions : suggestions;
        ss.forEach(suggestion => {
            if (!suggestion.is_match_decision_made)
                urls.push({product_url: suggestion.product_url});
        });

        if (urls.length !== 0) {
            props.ignoreCompetitorSuggestion(urls);
            setSelectedSuggestions([]);
        }

    };

    const suggestionImageTemplate = (rowData) => {
        return <div>
            <span className="p-column-title">{t('product.image')}</span>
            <img referrerPolicy='no-referrer' className="img-fluid-thumb" src={rowData.image} alt=""/>
        </div>;
    }
    const suggestionNameTemplate = (rowData) => {
        return <div>
            <span className="p-column-title">{t('table_headers.name')}</span>
            {rowData.product_url &&
                <div style={{marginBottom: '.25em'}}>
                    <a href={rowData.product_url} target="_blank" rel="noopener noreferrer">
                        {getHostName(rowData.product_url)}
                    </a>
                    {rowData.is_match_decision_made && rowData.is_matched &&
                        <span className="p-tag" style={{...TAG_STYLE, marginLeft: '.5em', background: SOFT.green.bg, color: SOFT.green.fg}}>{t('suggestion.status_matched')}</span>}
                    {rowData.is_match_decision_made && !rowData.is_matched && !rowData.removed_during_cleanup &&
                        <span className="p-tag" style={{...TAG_STYLE, marginLeft: '.5em', background: SOFT.gray.bg, color: SOFT.gray.fg}}>{t('suggestion.status_unmatched')}</span>}
                    {rowData.source_campaign_name &&
                        <span className="p-tag" style={{...TAG_STYLE, marginLeft: '.5em', background: SOFT.blue.bg, color: SOFT.blue.fg}}>
                            {t('suggestion.source_campaign', {name: rowData.source_campaign_name})}</span>}
                </div>}
            {highlightCodes(rowData.name)}
            {rowData.removed_during_cleanup &&
                <span className="p-tag" style={{...TAG_STYLE, marginLeft: '0.5em', background: SOFT.amber.bg, color: SOFT.amber.fg}}>
                    {t('suggestion.removed_during_cleanup')}</span>}
            {rowData.brand && <div><b>{t('product.brand')} : </b>{rowData.brand}</div>}
            {rowData.category && <div><b>{t('product.category')} : </b>{rowData.category}</div>}
            {rowData.sku && <div><b>{t('product.sku')} : </b>{highlightCodes(rowData.sku)}</div>}
            {rowData.mpn && <div><b>{t('product.mpn')} : </b>{highlightCodes(rowData.mpn)}</div>}
            {rowData.ean && <div><b>{t('product.ean')} : </b>{highlightCodes(rowData.ean)}</div>}
            {rowData.upc && <div><b>{t('product.upc')} : </b>{highlightCodes(rowData.upc)}</div>}
            {rowData.asin && <div><b>{t('product.asin')} : </b>{highlightCodes(rowData.asin)}</div>}
            {rowData.barcode && <div><b>{t('product.barcode')} : </b>{highlightCodes(rowData.barcode)}</div>}
            {rowData.price && rowData.price.created_at &&
                <div style={{color: '#6b7280', fontSize: '.8rem', marginTop: '.25em'}}>
                    {t('suggestion.suggested_at')}: {toDateTimeString(rowData.price.created_at)}
                </div>}
        </div>;
    };
    const suggestionPriceTemplate = (rowData) => <div className="p-text-nowrap p-text-right">
        <span className="p-column-title">{t('product_detail.price')}</span>
        {rowData.price && rowData.price.value ? (formatCurrency(rowData.price.value, rowData.price.currency)) : ""}
        <div>
            {rowData.used_base_spider && user().is_admin ? <Button icon="pi pi-exclamation-triangle" className="imageButton" tooltip={rowData.used_base_spider} style={{color: 'orange'}}/> : null}
            {rowData.price && rowData.price.stock > 0
                ? <span className="p-tag" style={{...TAG_STYLE, background: SOFT.green.bg, color: SOFT.green.fg, border: 'none'}}>{t('product.stock_in')}</span>
                : <span className="p-tag" style={{...TAG_STYLE, background: SOFT.red.bg, color: SOFT.red.fg, border: 'none'}}>{t('product.stock_out')}</span>}
        </div>
    </div>;

    const scoreSoft = (s) => s >= 0.8 ? SOFT.green : (s >= 0.5 ? SOFT.amber : SOFT.red);

    const suggestionScoreTemplate = (rowData) => {
        const hasScore = rowData.match_score != null;
        const hasBreakdown = !!(rowData.match_score_breakdown && rowData.match_score_breakdown.components);
        const entry = hasScore ? scoreSoft(rowData.match_score) : SOFT.gray;
        const label = hasScore ? formatDecimal(rowData.match_score * 100) : '—';
        return (
            <div className="p-text-right">
                <span className="p-column-title">{t('product_detail.match_score')}</span>
                <span
                    onClick={(e) => { e.stopPropagation(); if (hasScore) setCompareRow(rowData); }}
                    title={hasScore ? t('suggestion.compare') : undefined}
                    className={hasScore ? 'pux-score-btn' : undefined}
                    style={{display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center',
                            minWidth: '2.6em', padding: '.22em .5em', borderRadius: '10px',
                            color: entry.fg, fontWeight: 600, background: entry.bg,
                            cursor: hasScore ? 'pointer' : 'default'}}>
                    {label}
                    {hasScore && <i className="pi pi-search-plus" style={{fontSize: '.62rem', opacity: .8}}/>}
                </span>
            </div>
        );
    };

    const suggestionStrengthTemplate = (rowData) => {
        const level = STRENGTHS[rowData.match_strength];
        return (
            <div>
                <span className="p-column-title">{t('suggestion.match_strength')}</span>
                {level && <span title={t('suggestion.strength_' + rowData.match_strength + '_hint')}
                                style={{...TAG_STYLE, color: level.soft.fg, background: level.soft.bg, fontWeight: 600}}>
                    {t('suggestion.strength_' + rowData.match_strength)}
                </span>}
            </div>
        );
    };

    // Suggestions added before the label existed have none; they show no column or filter.
    // Profile campaigns show match reasons instead of strengths.
    const hasStrengths = !groupedView && (props.selectedProduct.competitor_suggestions || []).some(suggestion => suggestion.match_strength);
    const matchedView = filters.is_matched === true;
    // Without scores (the scoring job is on demand) the strongest matches still come first.
    const isSortedByStrength = hasStrengths && !suggestions.some(suggestion => suggestion.match_score != null);

    const suggestionActionTemplate = (rowData) => {
        return <div className="p-text-right" style={{whiteSpace: 'normal'}}>
            <span className="p-column-title">{t('table_headers.action')}</span>
            <Button icon="pi pi-check-circle" className="p-button-success p-button-sm" tooltip={t('actions.add_as_competitor')} tooltipOptions={{position: 'bottom'}} style={{marginRight: '.5em', marginBottom: '.25em', background: SOFT.green.btnBg, borderColor: SOFT.green.btnBg, color: SOFT.green.btnFg}} onClick={(e) => { e.stopPropagation(); addAsCompetitor(rowData); }}/>
            <Button icon="pi pi-times-circle" className="p-button-warning p-button-sm" tooltip={t('actions.mark_not_competitor')} tooltipOptions={{position: 'bottom'}} style={{marginBottom: '.25em', background: SOFT.amber.btnBg, borderColor: SOFT.amber.btnBg, color: SOFT.amber.btnFg}} onClick={(e) => { e.stopPropagation(); ignoreCompetitorSuggestion(rowData); }}/>
            {rowData.is_matched && !rowData.removed_during_cleanup &&
                <Button icon="pi pi-replay" className="p-button-danger p-button-sm"
                    tooltip={t('suggestion.revert_match')} tooltipOptions={{position: 'bottom'}}
                    style={{marginLeft: '.5em', marginBottom: '.25em', background: SOFT.red.btnBg, borderColor: SOFT.red.btnBg, color: SOFT.red.btnFg}}
                    onClick={(e) => { e.stopPropagation(); cleanupService.removeMatchedCompetitorForCleanup(
                        props.selectedProduct.id, rowData.product_url)
                        .then(() => props.refreshProducts && props.refreshProducts())
                        .catch(err => setError(err)); }} />}
            {rowData.removed_during_cleanup &&
                <Button icon="pi pi-undo" className="p-button-secondary p-button-sm"
                    tooltip={t('suggestion.cleanup_restore')} tooltipOptions={{position: 'bottom'}}
                    style={{marginLeft: '.5em', marginBottom: '.25em', background: SOFT.gray.btnBg, borderColor: SOFT.gray.btnBg, color: SOFT.gray.btnFg}}
                    onClick={(e) => { e.stopPropagation(); cleanupService.restoreCleanupRemovedSuggestion(
                        props.selectedProduct.id, rowData.product_url)
                        .then(() => props.refreshProducts && props.refreshProducts())
                        .catch(err => setError(err)); }} />}
        </div>;
    }

    const acceptRows = (rows) => props.addCompetitor(rows.map(row => ({product_url: row.product_url})), null);
    const rejectRows = (rows) => props.ignoreCompetitorSuggestion(rows.map(row => ({product_url: row.product_url})));
    const revertRow = (row) => cleanupService.removeMatchedCompetitorForCleanup(
        props.selectedProduct.id, row.product_url)
        .then(() => props.refreshProducts && props.refreshProducts())
        .catch(err => setError(err));

    const restoreRow = (row) => cleanupService.restoreCleanupRemovedSuggestion(
        props.selectedProduct.id, row.product_url)
        .then(() => props.refreshProducts && props.refreshProducts())
        .catch(err => setError(err));

    const decisionColor = (rowData) => {
        if (selectedSuggestions.some(s => s.product_url === rowData.product_url))
            return {'pux-row-selected': true};
        return "";
    };

    return (

        <React.Fragment>
            <SiteSearchSummary productId={props.selectedProduct.id} target="competitor"
                               hasSuggestions={(props.selectedProduct.competitor_suggestions || [])
                                   .some(suggestion => !suggestion.is_match_decision_made)}/>
            {/* Preview deep-link: only the operation's affected suggestions are shown. */}
            {props.affectedUrls && props.affectedUrls.length > 0 &&
                <Message severity="info" style={{display: 'flex', marginBottom: 10, width: '100%'}}
                         content={
                             <span style={{display: 'inline-flex', alignItems: 'center', gap: 10}}>
                                 {t('suggestion.preview_affected', {count: props.affectedUrls.length})}
                                 {props.clearAffectedUrls &&
                                     <Button label={t('suggestion.preview_show_all')} className="p-button-text p-button-sm"
                                             onClick={() => props.clearAffectedUrls()}/>}
                             </span>
                         }/>}
            {/* Filter chips (product-list format): decision state + base-spider, with a global filter. */}
            <div className="pux-fchips">
                <span className="p-input-icon-left">
                    <i className="pi pi-search"/>
                    <InputText id="name" name="name" value={filters.name} onChange={onChange}
                               placeholder={t('product.globalFilter')} style={{width: '14em'}}/>
                </span>
                <span className="pux-fchips-sep"/>
                {!groupedView && matchedOptions.map(opt => filterChip(opt, 'is_matched'))}
                {!groupedView && isAdmin() && <span className="pux-fchips-sep"/>}
                {isAdmin() && usedBaseSpiderOptions.map(opt => filterChip(opt, 'used_base_spider'))}
                {hasStrengths && <span className="pux-fchips-sep"/>}
                {hasStrengths && <span style={{fontSize: '.85rem', color: '#6b7280', alignSelf: 'center'}}>{t('suggestion.match_strength')}</span>}
                {hasStrengths && strengthOptions.map(opt => filterChip(opt, 'match_strength'))}
                {!groupedView && <span className="pux-fchips-sep"/>}
                {!groupedView && <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                    <span style={{fontSize: '.85rem', color: '#6b7280'}}>{t('suggestion.min_score_filter')}</span>
                    <InputNumber value={filters.min_score}
                                 onChange={(e) => setFilters({...filters, min_score: e.value})}
                                 min={0} max={100} inputStyle={{width: '4.5em'}}/>
                </span>}
            </div>

            {groupedView &&
                <SuggestionGroups t={t} suggestions={suggestions} isAdmin={isAdmin()}
                                  onAccept={acceptRows} onReject={rejectRows} onRevert={revertRow}
                                  onRestore={restoreRow}/>}

            {/* Selection bar (appears once rows are checked), mirroring the product list. */}
            {!groupedView && <ProductSelectionBar
                t={t}
                count={selectedSuggestions.length}
                primaryActions={[
                    {key: 'add', label: t('actions.add_as_competitor'), icon: 'pi pi-check-circle',
                        className: 'p-button-success', command: addAllAsCompetitor},
                    {key: 'reject', label: t('actions.mark_not_competitor'), icon: 'pi pi-times-circle',
                        className: 'p-button-warning', command: ignoreAllCompetitorSuggestion},
                ]}
                onClear={() => setSelectedSuggestions([])}
            />}

            {!groupedView && <DataTable value={suggestions}
                       responsive={true}
                       resizableColumns={true} columnResizeMode="fit"
                       sortField={isSortedByStrength ? 'match_strength_rank' : 'match_score'}
                       sortOrder={isSortedByStrength || matchedView ? 1 : -1}
                       rowClassName={decisionColor}
                       dataKey="product_url"
                       selectionMode="multiple" metaKeySelection={false}
                       selection={selectedSuggestions} onSelectionChange={(e) => setSelectedSuggestions(e.value)}>
                <Column selectionMode="multiple" headerStyle={{width: '3em'}}/>
                <Column body={IndexTemplate} headerStyle={{width: '3em'}}/>
                <Column field="image" body={suggestionImageTemplate} header={t('product.image')} headerStyle={{width: '12%'}}/>
                <Column field="name" body={suggestionNameTemplate} header={t('table_headers.name')} headerStyle={{width: '42%'}}/>
                <Column field="price" body={suggestionPriceTemplate} header={t('product_detail.price')} headerStyle={{textAlign: "right", width: '12%'}}/>
                {hasStrengths && <Column field="match_strength" body={suggestionStrengthTemplate} header={t('suggestion.match_strength')} headerStyle={{width: '90px'}} style={{width: '90px'}}/>}
                <Column field="match_score" body={suggestionScoreTemplate} header={t('product_detail.match_score')} headerStyle={{textAlign:"right", width:'110px'}} style={{width:'110px'}}/>
                <Column body={suggestionActionTemplate} header={t('table_headers.action')} headerStyle={{textAlign: 'right',width: '170px'}} className="preventSelect"/>
            </DataTable>}

            <Dialog header={t('suggestion.compare')} visible={!!compareRow}
                    style={{width: '660px', maxWidth: '95vw'}} modal onHide={() => setCompareRow(null)}>
                {compareRow && (() => {
                    const bd = compareRow.match_score_breakdown || {};
                    const fields = bd.fields || [];
                    const comps = bd.components || [];
                    const attrs = fields.filter(f => f.group === 'attr');
                    const codes = fields.filter(f => f.group === 'code');
                    const stColor = {match: ['#dcfce7', '#166534'], differ: ['#fee2e2', '#991b1b'],
                        na: ['#fef9c3', '#854d0e'], partial: ['#fef9c3', '#854d0e'], missing: ['#f3f4f6', '#6b7280']};
                    const FIELD_KEY = {'load/speed index': 'load_speed_index'};
                    const fieldLabel = (n) => t('suggestion.field.' + (FIELD_KEY[n] || n), n);
                    const pill = (st) => { const c = stColor[st] || stColor.missing;
                        return <span style={{background: c[0], color: c[1], fontSize: '.72rem', fontWeight: 600,
                            padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap'}}>{t('suggestion.cmp_status.' + st, st)}</span>; };
                    const row = (f) => (
                        <tr key={f.group + f.name} style={{borderTop: '1px solid #f1f5f9'}}>
                            <td style={{padding: '7px 6px', color: '#6b7280'}}>{fieldLabel(f.name)}</td>
                            <td style={{padding: '7px 6px', wordBreak: 'break-all'}}>{f.product || '—'}</td>
                            <td style={{padding: '7px 6px', wordBreak: 'break-all'}}>{f.suggestion || '—'}</td>
                            <td style={{padding: '7px 6px', textAlign: 'right'}}>{pill(f.status)}</td>
                        </tr>
                    );
                    const bar = (c) => {
                        const pct = Math.round((c.score || 0) * 100);
                        return <div key={c.key} style={{marginBottom: 8}}>
                            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '.85rem', marginBottom: 2}}>
                                <span>{t('suggestion.score_component.' + c.key, c.key)}</span><span style={{fontWeight: 600}}>{pct}%</span></div>
                            <div style={{height: 6, background: '#e5e7eb', borderRadius: 4}}>
                                <div style={{height: 6, width: pct + '%', borderRadius: 4, background: c.type === 'penalty' ? '#dc2626' : '#3b82f6'}}/></div>
                        </div>; };
                    const secHeader = (txt) => <div style={{fontSize: '.72rem', color: '#9ca3af', margin: '16px 0 6px', fontWeight: 600}}>{txt}</div>;
                    const grpHeader = (txt) => <tr><td colSpan={4} style={{padding: '12px 6px 2px', fontSize: '.72rem', color: '#9ca3af'}}>{txt}</td></tr>;
                    const entry = compareRow.match_score != null ? scoreSoft(compareRow.match_score) : SOFT.gray;
                    const thumb = (src) => src
                        ? <img src={src} alt="" style={{width: 72, height: 72, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff'}}/>
                        : <div style={{width: 72, height: 72, border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1'}}><i className="pi pi-image"/></div>;
                    const prod = props.selectedProduct || {};
                    const ownUrl = ((prod.competitors || []).find(c => c.is_mine) || {}).product_url;
                    const brandComp = comps.find(c => c.key === 'brand_in_title');
                    const brandRow = prod.brand ? {group: 'attr', name: 'brand', product: prod.brand,
                        suggestion: brandComp ? prod.brand : '', status: brandComp ? 'match' : 'missing'} : null;
                    const titleLink = (txt, url) => url
                        ? <a href={url} target="_blank" rel="noopener noreferrer">{txt || '—'}</a>
                        : (txt || '—');
                    return <div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: '.9rem'}}>
                            <span style={{fontWeight: 600}}>{prod.name}</span>
                            <i className="pi pi-arrows-h" style={{color: '#9ca3af'}}/>
                            <span style={{color: '#6b7280'}}>{getHostName(compareRow.product_url)}</span>
                            {compareRow.match_score != null &&
                                <span style={{marginLeft: 'auto', background: entry.bg, color: entry.fg, fontWeight: 600,
                                    padding: '.15em .55em', borderRadius: '10px'}}>{formatDecimal(compareRow.match_score * 100)}</span>}
                        </div>
                        {bd.method === 'hard_identifier' &&
                            <div style={{fontSize: '.8rem', color: '#166534'}}>{t('suggestion.matched_by_code')}</div>}

                        {comps.length > 0 && <div>{secHeader(t('suggestion.cmp_group_signals'))}{comps.map(bar)}</div>}

                        {secHeader(t('suggestion.cmp_group_image'))}
                        <div style={{display: 'flex', gap: 18}}>
                            <div style={{textAlign: 'center'}}>{thumb(prod.image)}<div style={{fontSize: '.7rem', color: '#9ca3af', marginTop: 3}}>{t('suggestion.cmp_product')}</div></div>
                            <div style={{textAlign: 'center'}}>{thumb(compareRow.image)}<div style={{fontSize: '.7rem', color: '#9ca3af', marginTop: 3}}>{t('suggestion.cmp_suggestion')}</div></div>
                        </div>

                        {secHeader(t('suggestion.cmp_group_title'))}
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, fontSize: '.82rem'}}>
                            <div><div style={{color: '#9ca3af', fontSize: '.7rem'}}>{t('suggestion.cmp_product')}</div>{titleLink(prod.name, ownUrl)}</div>
                            <div><div style={{color: '#9ca3af', fontSize: '.7rem'}}>{t('suggestion.cmp_suggestion')}</div>{titleLink(compareRow.name, compareRow.product_url)}</div>
                        </div>

                        {fields.length > 0
                            ? <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '.85rem', tableLayout: 'fixed', marginTop: 10}}>
                                <colgroup><col style={{width: '24%'}}/><col style={{width: '30%'}}/><col style={{width: '30%'}}/><col style={{width: '16%'}}/></colgroup>
                                <thead><tr style={{color: '#9ca3af', fontSize: '.7rem', textAlign: 'left'}}>
                                    <th style={{padding: '2px 6px', fontWeight: 400}}>{t('suggestion.cmp_field')}</th>
                                    <th style={{padding: '2px 6px', fontWeight: 400}}>{t('suggestion.cmp_product')}</th>
                                    <th style={{padding: '2px 6px', fontWeight: 400}}>{t('suggestion.cmp_suggestion')}</th>
                                    <th style={{padding: '2px 6px', fontWeight: 400, textAlign: 'right'}}>{t('suggestion.cmp_status_h')}</th>
                                </tr></thead>
                                <tbody>
                                    {(brandRow || attrs.length > 0) && grpHeader(t('suggestion.cmp_group_attr'))}
                                    {brandRow && row(brandRow)}
                                    {attrs.map(row)}
                                    {codes.length > 0 && grpHeader(t('suggestion.cmp_group_code'))}
                                    {codes.map(row)}
                                </tbody>
                            </table>
                            : <div style={{color: '#6b7280', fontSize: '.85rem', marginTop: 10}}>{t('suggestion.cmp_no_fields')}</div>}
                    </div>;
                })()}
            </Dialog>

        </React.Fragment>

    );

};
export default CompetitorSuggestion;
